import React, { useCallback, useEffect } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { ConfirmDeleteDialog } from 'src/components/shared/dialog/ConfirmDeleteDialog';
import { RootState } from 'src/features';
import { actions } from 'src/features/resort';
import { throttle } from 'src/lib/utils';

interface OptionalDeleteHistoryDialogProps {
  basePath: string;
}

export const OptionalDeleteHistoryDialog: React.FC<OptionalDeleteHistoryDialogProps> = ({
  basePath
}) => {
  const { t } = useTranslation(['resort', 'translation']);
  const history = useHistory();
  const { deleteStatus } = useSelector((state: RootState) => state.resort);
  const dispatch = useDispatch();
  const { raid } = useParams<{ raid: string }>();

  useEffect(() => {
    return () => {
      dispatch(actions.resetDeleteStatus());
    };
  }, [dispatch]);

  useEffect(() => {
    if (deleteStatus?.state === 'ok') {
      history.replace(`${basePath}/history-delete-succeeded`);
    }
  }, [basePath, deleteStatus, history]);

  const handleConfirmClick = useCallback(() => {
    dispatch(actions.deleteHistory(raid));
  }, [dispatch, raid]);

  const handleCancelClick = useCallback(
    throttle(() => {
      history.goBack();
    }, 1000),
    [history]
  );

  const isLoading = deleteStatus?.state === 'pending';
  const errorMessage =
    deleteStatus?.state === 'error' ? getErrorDescription(t, deleteStatus.error) : undefined;

  return (
    <ConfirmDeleteDialog
      title={t('dialog_delete_history')}
      description={t('dialog_delete_history_desc')}
      confirmLabel={t('translation:button_delete')}
      cancelLabel={t('translation:dialog_button_cancel')}
      errorMessage={errorMessage}
      onConfirmClick={handleConfirmClick}
      onCancelClick={handleCancelClick}
      isLoading={isLoading}
    />
  );
};

// helper function
function getErrorDescription(t: TFunction<('resort' | 'translation')[]>, error?: string): string {
  switch (error) {
    case 'unstable-network':
      return t('translation:error_unstable_network');
    default:
      return t('translation:error_unknown');
  }
}
